import { useState } from 'react';
import { api } from '../api';
import { Banner, Card, Empty, Loading, Modal, formatKobo, useAsync, when } from '../ui';

/**
 * The flagged queue.
 *
 * Every decision here requires a written reason, and that reason goes into the
 * append-only audit log against the reviewer's name. That is deliberate
 * friction: this is the one screen where a human overrides an automated
 * fraud signal and releases money.
 */
export function Flagged() {
  const { data, error, loading, reload } = useAsync(() => api.flagged(), []);
  const [target, setTarget] = useState<{ id: string; decision: 'approve' | 'reject'; amount: string; driver: string } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Bulk approval: only referrals flagged for missing screening, or a phone mismatch an admin already accepted.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  async function submit() {
    if (!target || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.review(target.id, target.decision, reason.trim());
      setNote(`Referral ${target.id.slice(0, 8)} ${target.decision === 'approve' ? 'approved' : 'rejected'}.`);
      setTarget(null);
      setReason('');
      reload();
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submitBulk() {
    const chosen = rowsAll.filter((r) => picked.has(r.referral_id));
    if (chosen.length === 0 || bulkReason.trim().length < 10) return;
    setBulkBusy(true);
    setBulkError(null);
    try {
      const total = chosen.reduce((sum, r) => sum + BigInt(r.amount), 0n).toString();
      const r = await api.bulkApprove({
        referral_ids: chosen.map((x) => x.referral_id),
        reason: bulkReason.trim(),
        expected_count: chosen.length,
        expected_total: total,
      });
      setNote(
        r.failed.length === 0
          ? `Approved ${r.approved} referral${r.approved === 1 ? '' : 's'}, ${formatKobo(r.total)} in all. They are ready to release.`
          : `Approved ${r.approved}; ${r.failed.length} could not be approved: ${r.failed.map((f) => f.message).join('; ')}`,
      );
      setBulkOpen(false);
      setBulkReason('');
      setPicked(new Set());
      reload();
    } catch (e) {
      setBulkError((e as Error).message);
    } finally {
      setBulkBusy(false);
    }
  }

  if (loading) return <Loading label="Loading queue" />;
  if (error) return <Banner tone="danger" title="Could not load the queue">{error}</Banner>;

  const rows = data?.referrals ?? [];
  const rowsAll = rows;
  const clean = rows.filter((r) => r.bulk_approvable);
  const pickedRows = rows.filter((r) => picked.has(r.referral_id));
  const pickedTotal = pickedRows.reduce((sum, r) => sum + BigInt(r.amount), 0n).toString();
  const toggle = (id: string) =>
    setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="stack">
      {note && <Banner tone="ok">{note}</Banner>}

      {rows.length === 0 ? (
        <Card><Empty title="Nothing awaiting review" hint="Flagged referrals appear here when a fraud signal needs a human decision." /></Card>
      ) : (
        <Card flush>
          {clean.length > 0 && (
            <div className="card-head">
              <span className="small">
                <strong>{clean.length}</strong>{' '}
                <span className="muted">
                  {clean.length === 1 ? 'referral has' : 'referrals have'} NIBSS’s name the same as inDrive’s and {clean.length === 1 ? 'is' : 'are'} flagged
                  only for missing sanctions screening{clean.some((r) => r.phone_accepted) ? ' or a phone mismatch that was accepted' : ''},
                  so {clean.length === 1 ? 'it' : 'they'} can be approved together. Anything else, including a name that differs or is not on file, needs its own look.
                </span>
              </span>
              <div className="spacer" />
              <button className="btn sm" onClick={() => setPicked(picked.size === clean.length ? new Set() : new Set(clean.map((r) => r.referral_id)))}>
                {picked.size === clean.length ? 'Clear selection' : `Select all ${clean.length}`}
              </button>
              <button className="btn sm primary" disabled={picked.size === 0} onClick={() => { setBulkError(null); setBulkOpen(true); }}>
                Approve selected{picked.size > 0 ? ` (${picked.size})` : ''}
              </button>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 28 }} /><th>Referral</th><th>Driver</th><th className="num">Commission</th>
                  <th className="num">Risk</th><th>Why it was flagged</th>
                  <th className="num">Waiting</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.referral_id}>
                    <td>
                      {r.bulk_approvable && (
                        <input
                          type="checkbox"
                          aria-label={`Select referral ${r.referral_id.slice(0, 8)}`}
                          checked={picked.has(r.referral_id)}
                          onChange={() => toggle(r.referral_id)}
                        />
                      )}
                    </td>
                    <td className="mono">{r.referral_id.slice(0, 8)}</td>
                    <td className="small">
                      <div>{r.referring_driver_id ?? '—'}</div>
                      <div className="muted mono">{r.destination_masked}</div>
                    </td>
                    <td className="num">{r.amount_display}</td>
                    <td className="num">
                      <span className={`pill ${(r.fraud?.score ?? 0) >= 60 ? 'danger' : 'warn'} plain`}>{r.fraud?.score ?? '—'}</span>
                    </td>
                    <td className="small">
                      {r.reasons_explained.filter((x) => !x.code.startsWith('lifecycle')).map((x) => (
                        <div key={x.code} style={{ marginBottom: 2 }}>{x.label}</div>
                      ))}
                      <Names r={r} />
                    </td>
                    <td className="num small muted">{when(r.created_at, { time: false })}</td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn sm"
                        onClick={() => setTarget({ id: r.referral_id, decision: 'approve', amount: r.amount_display, driver: r.referring_driver_id ?? '—' })}
                      >
                        Approve
                      </button>{' '}
                      <button
                        className="btn sm danger"
                        onClick={() => setTarget({ id: r.referral_id, decision: 'reject', amount: r.amount_display, driver: r.referring_driver_id ?? '—' })}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {target && (
        <Modal
          title={target.decision === 'approve' ? 'Approve this referral' : 'Reject this referral'}
          description={
            target.decision === 'approve'
              ? 'Approving clears it for payout. Your name and reason are recorded permanently.'
              : 'Rejecting is final — a rejected referral can never be paid.'
          }
          onClose={() => { setTarget(null); setReason(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setTarget(null); setReason(''); }}>Cancel</button>
              <button
                className={`btn ${target.decision === 'approve' ? 'primary' : 'danger'}`}
                disabled={busy || reason.trim().length < 3}
                onClick={submit}
              >
                {busy ? <span className="spin" /> : target.decision === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </>
          }
        >
          <dl className="dl">
            <dt>Referral</dt><dd className="mono">{target.id.slice(0, 8)}</dd>
            <dt>Driver</dt><dd>{target.driver}</dd>
            <dt>Commission</dt><dd style={{ fontWeight: 600 }}>{target.amount}</dd>
          </dl>
          <div className="field">
            <label htmlFor="review-reason">Reason (required)</label>
            <textarea
              id="review-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                target.decision === 'approve'
                  ? 'e.g. Shared device confirmed as a family handset; both drivers verified independently.'
                  : 'e.g. Both accounts trace to the same operator; referral is not genuine.'
              }
            />
            <span className="hint">Written to the audit log against your account. At least 3 characters.</span>
          </div>
        </Modal>
      )}

      {bulkOpen && (
        <Modal
          title={`Approve ${pickedRows.length} referral${pickedRows.length === 1 ? '' : 's'}?`}
          description="Approving clears them for payout; nothing is sent yet. Your name and reason are recorded against each one."
          onClose={() => { setBulkOpen(false); setBulkReason(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setBulkOpen(false); setBulkReason(''); }}>Cancel</button>
              <button className="btn primary" disabled={bulkBusy || bulkReason.trim().length < 10} onClick={submitBulk}>
                {bulkBusy ? <span className="spin" /> : `Approve ${pickedRows.length} · ${formatKobo(pickedTotal)}`}
              </button>
            </>
          }
        >
          {bulkError && <Banner tone="danger" title="Nothing was approved">{bulkError}</Banner>}
          <dl className="dl">
            <dt>Referrals</dt><dd style={{ fontWeight: 600 }}>{pickedRows.length}</dd>
            <dt>Total</dt><dd style={{ fontWeight: 600 }}>{formatKobo(pickedTotal)}</dd>
          </dl>
          <p className="small" style={{ lineHeight: 1.6 }}>
            Each of these was flagged only because we have no sanctions or PEP screening provider connected
            {pickedRows.some((r) => r.phone_accepted)
              ? <>, and <strong>{pickedRows.filter((r) => r.phone_accepted).length}</strong> also because their phone differs from the BVN — which an admin already accepted with a written reason</>
              : null}
            . Their identity, account-name and fraud checks all passed. If you have not checked the screening yourself, do that first.
          </p>
          <div className="field">
            <label htmlFor="bulk-reason">Reason (required)</label>
            <textarea
              id="bulk-reason"
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder="e.g. Sanctions screening is not connected; identity and account checks reviewed for the 1–15 Oct batch."
            />
            <span className="hint">Written to the audit log against your account. At least 10 characters.</span>
          </div>
        </Modal>
      )}
    </div>
  );
}

const NAME_VERDICT = {
  same: { tone: 'ok', label: 'Same name' },
  likely: { tone: 'warn', label: 'Close — check it' },
  different: { tone: 'danger', label: 'Different name' },
} as const;

/**
 * inDrive's name for the driver beside the one NIBSS gave at KYC. For a phone
 * mismatch this is the evidence that decides it, so it sits in the row rather
 * than one click away on the driver's page.
 */
function Names({ r }: { r: { indrive_name?: string | null; nibss_name?: string | null; name_verdict?: 'same' | 'likely' | 'different' | null } }) {
  if (!r.indrive_name && !r.nibss_name) return null;
  return (
    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--rule)' }}>
      <div><span className="muted">inDrive:</span> {r.indrive_name ?? <span className="muted">no name on file</span>}</div>
      <div><span className="muted">NIBSS:</span> {r.nibss_name ?? <span className="muted">not checked yet</span>}</div>
      {r.name_verdict && (
        <span className={`pill ${NAME_VERDICT[r.name_verdict].tone}`} style={{ marginTop: 4 }}>{NAME_VERDICT[r.name_verdict].label}</span>
      )}
    </div>
  );
}
